import React from 'react';
import { Field } from '../../components/ui';
import StepNav from './StepNav';

// Desktop "Setup Event" wizard: Function → Guests → Members, in one flow.
export default function SetupDesktop({ vm }) {
  const {
    step, saving, canFunctions, canGuests,
    functions, fnOptions, activeFn, activeFnId, setActiveFnId, pickExistingFunction,
    fnForm, setFn, saveFunction,
    guestForm, setGuest, saveGuest, guestsAdded,
    memberForm, setMember, saveMember, membersAdded,
    FUNCTION_TYPE_OPTIONS, goStep,
  } = vm;

  return (
    <div>
      <div className="page-head"><h1>Setup Event</h1></div>
      <StepNav vm={vm} />

      {/* ── Step 1: Add Function ─────────────────────────────────────────── */}
      {step === 1 && (
        <div className="card card-pad">
          {!canFunctions ? (
            <div className="empty">Only the Event Owner / Admin can add functions. Skip to step 2 to add guests.</div>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>Step 1 · Add a Function</h3>
              <div className="form-grid">
                <Field label="Function Name" value={fnForm.FunctionName} onChange={v => setFn('FunctionName', v)} required />
                <Field label="Type" type="select" value={fnForm.FunctionType} options={FUNCTION_TYPE_OPTIONS} onChange={v => setFn('FunctionType', v)} />
                {fnForm.FunctionType === 'Other' && <Field label="New type name" value={fnForm.FunctionTypeOther} onChange={v => setFn('FunctionTypeOther', v)} />}
                <Field label="Date" type="date" value={fnForm.FunctionDate} onChange={v => setFn('FunctionDate', v)} />
                <Field label="Time" type="time" value={fnForm.FunctionTime} onChange={v => setFn('FunctionTime', v)} />
                <Field label="Venue" value={fnForm.Venue} onChange={v => setFn('Venue', v)} />
                <Field label="City" value={fnForm.City} onChange={v => setFn('City', v)} />
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Venue Address" type="textarea" value={fnForm.VenueAddress} onChange={v => setFn('VenueAddress', v)} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="GPS Location (lat,lng or Maps link — shown to guests)" type="textarea" value={fnForm.GpsLocation} onChange={v => setFn('GpsLocation', v)} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="btn primary" disabled={saving} onClick={saveFunction}>
                  {saving ? 'Saving…' : 'Save & Add Guests →'}
                </button>
              </div>

              {functions.length > 0 && (
                <div style={{ marginTop: 16, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
                  <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>…or pick an existing function to add guests:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {functions.map(f => (
                      <button key={f.FunctionId} className="btn light sm" onClick={() => pickExistingFunction(f.FunctionId)}>
                        {f.FunctionName} →
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Step 2: Add Guests in the function ───────────────────────────── */}
      {step === 2 && (
        <div className="card card-pad">
          {!canGuests ? (
            <div className="empty">You don't have permission to add guests.</div>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>
                Step 2 · Add Guests {activeFn ? <span className="muted" style={{ fontWeight: 400 }}>into “{activeFn.FunctionName}”</span> : ''}
              </h3>
              <div className="form-grid">
                <Field label="Function" type="select" value={activeFnId} options={fnOptions} onChange={setActiveFnId} required />
                <Field label="Guest Name" value={guestForm.GuestName} onChange={v => setGuest('GuestName', v)} required />
                <Field label="Mobile Number" value={guestForm.MobileNumber} onChange={v => setGuest('MobileNumber', v)} />
                <Field label="Relation" value={guestForm.Relation} onChange={v => setGuest('Relation', v)} />
                <Field label="Expected Pax" type="number" value={guestForm.ExpectedCount} onChange={v => setGuest('ExpectedCount', v)} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                <button className="btn primary" disabled={saving} onClick={() => saveGuest({ keepGoing: true })}>
                  {saving ? 'Saving…' : '+ Add & Add Another'}
                </button>
                <button className="btn light" disabled={saving} onClick={() => saveGuest({ keepGoing: false })}>
                  Add & Go to Members →
                </button>
                <div style={{ flex: 1 }} />
                <span className="muted" style={{ fontSize: 13 }}>{guestsAdded} added this session</span>
              </div>
              <div style={{ marginTop: 12 }}>
                <button className="btn light sm" onClick={() => goStep(3)}>Skip to Members →</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Step 3: Add Members for the event ────────────────────────────── */}
      {step === 3 && (
        <div className="card card-pad">
          {!canGuests ? (
            <div className="empty">You don't have permission to add members.</div>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>Step 3 · Add Members for the Event</h3>
              <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
                A member is a person invited to the event — pick which function they're invited to.
              </div>
              <div className="form-grid">
                <Field label="Function" type="select" value={memberForm.FunctionId} options={fnOptions} onChange={v => setMember('FunctionId', v)} required />
                <Field label="Member Name" value={memberForm.GuestName} onChange={v => setMember('GuestName', v)} required />
                <Field label="Mobile Number" value={memberForm.MobileNumber} onChange={v => setMember('MobileNumber', v)} />
                <Field label="Relation" value={memberForm.Relation} onChange={v => setMember('Relation', v)} />
                <Field label="Expected Pax" type="number" value={memberForm.ExpectedCount} onChange={v => setMember('ExpectedCount', v)} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                <button className="btn primary" disabled={saving} onClick={() => saveMember({ keepGoing: true })}>
                  {saving ? 'Saving…' : '+ Add & Add Another'}
                </button>
                <button className="btn light" disabled={saving} onClick={() => saveMember({ keepGoing: false })}>
                  Add & Finish
                </button>
                <div style={{ flex: 1 }} />
                <span className="muted" style={{ fontSize: 13 }}>{membersAdded} added this session</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
